/**
 * ProductBundleManager — Custom Element
 *
 * Manages the product bundle selection UI:
 *  - Product swatch selection per group (color switching)
 *  - Size/variant selection per group
 *  - Desktop gallery image swapping
 *  - Mobile preview image swapping
 *  - Validation (all groups must have product + size selected & in stock)
 *  - Add-to-cart with hidden _bundleItems / _bundleItemNames properties
 *  - "More details" popup modal
 */

if (!customElements.get('product-bundle-manager')) {
  class ProductBundleManager extends HTMLElement {
    constructor() {
      super();

      this.bundleGroups = [...this.querySelectorAll('.product-bundle__group')];
      this.addToCartButton = this.querySelector('.product-bundle__add-to-cart');
      this.moreDetailsButtons = [...this.querySelectorAll('.product-bundle__group-details-link')];
      this.popup = this.querySelector('.product-bundle__popup-overlay');
      this.popupCloseButton = this.querySelector('.product-bundle__popup-close');
      this.priceWrapper = document.querySelector('[data-bundle-price-wrapper]');
      this.selectedVariants = [];

      // Bundle discount configuration from metaobject
      this.bundleDiscount = parseFloat(this.dataset.bundleDiscount) || 0;
      this.memberBundleDiscount = parseFloat(this.dataset.memberBundleDiscount) || 0;
      this.isMember = this.dataset.isMember === 'true';
    }

    connectedCallback() {
      this.setSelectedVariants();
      this.setupBundleGroupEvents();
      this.setupAddToCart();
      this.setupPopup();
      this.setupGalleryArrows();
      this.setupDesktopGroupFocus();
      this.setupDesktopScrollObserver();
      this.updateBundlePrice();
    }

    /* ------------------------------------------------------------------
       Event setup
    ------------------------------------------------------------------ */

    setupGalleryArrows() {
      const gallery = document.querySelector('.product-bundle__gallery');
      if (!gallery) return;

      gallery.addEventListener('click', (e) => {
        const arrow = e.target.closest('.product-bundle__arrow');
        if (!arrow) return;

        const slideRow = arrow.closest('.product-bundle__slide-row');
        if (!slideRow) return;

        const slides = [...slideRow.querySelectorAll('.product-bundle__slide')];
        const count = slides.length;
        if (count <= 1) return;

        let currentIndex = parseInt(slideRow.dataset.slideIndex) || 0;

        if (arrow.classList.contains('product-bundle__arrow--next')) {
          currentIndex = (currentIndex + 1) % count;
        } else {
          currentIndex = (currentIndex - 1 + count) % count;
        }

        slides.forEach((s, i) => s.classList.toggle('is-active', i === currentIndex));
        slideRow.dataset.slideIndex = currentIndex;
      });
    }

    setupBundleGroupEvents() {
      this.bundleGroups.forEach((group) => {
        // Product swatch click (color switching)
        const swatches = [...group.querySelectorAll('.product-bundle__swatch')];
        swatches.forEach((swatch) => {
          swatch.addEventListener('click', () => {
            const productId = swatch.dataset.productId;
            const groupIndex = group.dataset.index;

            // Update swatch selection
            this.setActive(swatches, swatch, 'is-selected');

            // Show matching size options, hide others
            const optionPanels = [...group.querySelectorAll('.product-bundle__options')];
            const matchingPanel = optionPanels.find((p) => p.dataset.productId === productId);
            this.setActive(optionPanels, matchingPanel, 'is-selected');

            // Update variant/color label with specs.colour metafield
            const variantLabel = group.querySelector('.product-bundle__variant-label span');
            if (variantLabel) {
              variantLabel.textContent = swatch.dataset.colour || swatch.dataset.title;
            }

            // Swap mobile preview image (legacy)
            const preview = group.querySelector('.product-bundle__preview');
            if (preview) {
              preview.querySelector('img').src = swatch.dataset.featuredImage;
            }

            // Swap mobile carousel slides for this group
            const mobileCarousel = group.querySelector('.product-bundle__mobile-carousel');
            if (mobileCarousel) {
              const mobileSlides = [...mobileCarousel.querySelectorAll('.product-bundle__mobile-slides')];
              mobileSlides.forEach((slides) => {
                const isMatch = slides.dataset.productId === productId;
                slides.classList.toggle('show', isMatch);
                // Reset scroll position to first image
                if (isMatch) {
                  slides.scrollLeft = 0;
                }
              });
            }

            // Desktop: hide all groups' slide-rows first (single-group view)
            document.querySelectorAll('.product-bundle__gallery .product-bundle__slide-row.show').forEach(row => {
              row.classList.remove('show');
            });

            // Swap desktop gallery images for this group
            const gallerySlides = document.querySelectorAll(
              `.product-bundle__gallery .product-bundle__slide-row[data-group-index="${groupIndex}"]`
            );
            gallerySlides.forEach((slide) => {
              const isMatch = slide.dataset.productId === productId;
              slide.classList.toggle('show', isMatch);
              // Reset to first image when switching products
              if (isMatch) {
                slide.dataset.slideIndex = 0;
                const imgs = slide.querySelectorAll('.product-bundle__slide');
                imgs.forEach((img, i) => img.classList.toggle('is-active', i === 0));

                // Force-load lazy images in the newly revealed slide-row.
                // The row was display:none so the browser skipped lazy loading;
                // now that it's visible, ensure all images start loading immediately.
                slide.querySelectorAll('img[loading="lazy"]').forEach((img) => {
                  img.loading = 'eager';
                });
              }
            });

            // Update group header link
            const headerLink = group.querySelector('.product-bundle__group-title a');
            if (headerLink) {
              headerLink.href = swatch.dataset.url;
            }

            // Re-read state and validate
            this.setSelectedVariants();
            this.validateBundleManager();
          });
        });

        // Size radio click
        const sizeLabels = [...group.querySelectorAll('.product-bundle__size-label')];
        sizeLabels.forEach((label) => {
          label.addEventListener('click', (e) => {
            const radio = document.getElementById(label.getAttribute('for'));
            if (!radio) return;

            // If the item is out of stock, prevent selection entirely
            if (radio.dataset.variantAvailable === 'false' || radio.disabled) {
              e.preventDefault();
              return;
            }

            const optionPanel = label.closest('.product-bundle__options');
            const productId = optionPanel.dataset.productId;
            const groupIndex = group.dataset.index;

            this.selectedVariants[groupIndex] = {
              ...this.selectedVariants[groupIndex],
              productId: parseInt(productId),
              variantId: parseInt(radio.dataset.variantId),
              variantTitle: radio.value,
              variantAvailable: radio.dataset.variantAvailable === 'true',
              productTitle: optionPanel.dataset.productTitle || '',
              variantPrice: parseInt(radio.dataset.variantPrice) || 0
            };

            this.validateBundleManager();

            // Auto-scroll to the next product group (nudge user forward)
            const currentGroupIndex = parseInt(groupIndex);
            const nextGroup = this.bundleGroups[currentGroupIndex + 1];
            if (nextGroup) {
              setTimeout(() => {
                nextGroup.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, 150);
            } else {
              // Last group — scroll to the add-to-cart button
              if (this.addToCartButton) {
                setTimeout(() => {
                  this.addToCartButton.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 150);
              }
            }
          });
        });
      });
    }

    setupAddToCart() {
      if (!this.addToCartButton) return;

      this.addToCartButton.addEventListener('click', () => {
        if (this.addToCartButton.hasAttribute('disabled')) return;
        this.addBundleToCart();
      });
    }

    setupPopup() {
      if (!this.popup) return;

      // "More details" buttons
      this.moreDetailsButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const parentGroup = button.closest('.product-bundle__group');
          const selectedSwatch = parentGroup.querySelector('.product-bundle__swatch.is-selected');
          if (!selectedSwatch) return;

          const productId = selectedSwatch.dataset.productId;

          // Populate popup
          const popupTitle = this.popup.querySelector('.product-bundle__popup-title');
          const popupDesc = this.popup.querySelector('.product-bundle__popup-description');
          const popupImg = this.popup.querySelector('.product-bundle__popup-media img');

          if (popupTitle) popupTitle.textContent = selectedSwatch.dataset.title;
          if (popupDesc) popupDesc.innerHTML = selectedSwatch.dataset.description;
          if (popupImg) popupImg.src = selectedSwatch.dataset.featuredImage;

          // Load features from hidden additionals
          const additionals = parentGroup.querySelector(
            `.product-bundle__additionals[data-product-id="${productId}"] .product-bundle__features-content`
          );
          const tabContent = this.popup.querySelector('.product-bundle__popup-tab-content');
          const tabToggle = this.popup.querySelector('.product-bundle__popup-tab-toggle');
          const tabWrapper = this.popup.querySelector('.product-bundle__popup-tab');

          if (additionals && tabContent && additionals.innerHTML.trim()) {
            tabContent.innerHTML = additionals.innerHTML;
            if (tabWrapper) tabWrapper.style.display = '';
            // Auto-expand the tab
            if (tabToggle) {
              tabToggle.setAttribute('aria-expanded', 'true');
              tabContent.classList.add('is-open');
            }
          } else {
            if (tabWrapper) tabWrapper.style.display = 'none';
          }

          this.popup.classList.add('is-visible');
          document.body.style.overflow = 'hidden';
        });
      });

      // Close popup on X button
      if (this.popupCloseButton) {
        this.popupCloseButton.addEventListener('click', () => this.closePopup());
      }

      // Close popup on overlay click
      this.popup.addEventListener('click', (e) => {
        if (e.target === this.popup) this.closePopup();
      });

      // Close popup on Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.popup.classList.contains('is-visible')) {
          this.closePopup();
        }
      });

      // Tab toggle inside popup
      const tabToggle = this.popup.querySelector('.product-bundle__popup-tab-toggle');
      if (tabToggle) {
        tabToggle.addEventListener('click', () => {
          const isOpen = tabToggle.getAttribute('aria-expanded') === 'true';
          tabToggle.setAttribute('aria-expanded', !isOpen);
          const tabContent = this.popup.querySelector('.product-bundle__popup-tab-content');
          if (tabContent) tabContent.classList.toggle('is-open', !isOpen);
        });
      }
    }

    closePopup() {
      this.popup.classList.remove('is-visible');
      document.body.style.overflow = '';
    }

    /* ------------------------------------------------------------------
       State management
    ------------------------------------------------------------------ */

    setSelectedVariants() {
      this.selectedVariants = this.bundleGroups.map((group, index) => {
        const selectedSwatch = group.querySelector('.product-bundle__swatch.is-selected');
        const selectedOptions = group.querySelector('.product-bundle__options.is-selected');

        const productId = selectedSwatch?.dataset?.productId ?? null;
        let variantValue, variantId, variantAvailable, productTitle;

        let variantPrice = 0;

        if (selectedOptions) {
          const checkedRadio = selectedOptions.querySelector('input:checked');
          variantValue = checkedRadio?.value;
          variantId = checkedRadio?.dataset?.variantId;
          variantAvailable = checkedRadio?.dataset?.variantAvailable === 'true';
          productTitle = selectedOptions.dataset.productTitle || '';
          variantPrice = parseInt(checkedRadio?.dataset?.variantPrice) || 0;
        }

        return {
          index: index,
          productId: productId ? parseInt(productId) : null,
          variantId: variantId ? parseInt(variantId) : null,
          variantTitle: variantValue ?? null,
          variantAvailable: variantAvailable ?? false,
          productTitle: productTitle || (selectedSwatch?.dataset?.title ?? ''),
          variantPrice
        };
      });
    }

    setActive(list, activeItem, className) {
      list.forEach((item) => item.classList.remove(className));
      if (activeItem) activeItem.classList.add(className);
    }

    /* ------------------------------------------------------------------
       Desktop: single-group gallery view
       When a user interacts with any product group, the desktop gallery
       switches to show only that group's currently selected product.
    ------------------------------------------------------------------ */

    setupDesktopGroupFocus() {
      this.addEventListener('click', (e) => {
        const group = e.target.closest('.product-bundle__group');
        if (!group) return;
        const groupIndex = parseInt(group.dataset.index);
        this.showGroupInGallery(groupIndex);
      });
    }

    /**
     * Scroll-based gallery switching (desktop only).
     * Observes each product group; when a group scrolls into the
     * trigger zone (top 20–40 % of the viewport) the sticky gallery
     * automatically updates to show that group's selected product.
     */
    setupDesktopScrollObserver() {
      if (window.innerWidth <= 768) return;

      const triggerZone = {
        root: null,
        rootMargin: '-20% 0px -60% 0px',
        threshold: 0
      };

      this.scrollObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const groupIndex = parseInt(entry.target.dataset.index);
          this.showGroupInGallery(groupIndex);
        }
      }, triggerZone);

      this.bundleGroups.forEach((group) => this.scrollObserver.observe(group));
    }

    showGroupInGallery(groupIndex) {
      if (window.innerWidth <= 768) return;
      const gallery = document.querySelector('.product-bundle__gallery');
      if (!gallery) return;

      // If this group is already the only one visible, do nothing
      // (avoids resetting slide index on size clicks within the same group)
      const visibleRows = gallery.querySelectorAll('.product-bundle__slide-row.show');
      if (
        visibleRows.length === 1 &&
        parseInt(visibleRows[0].dataset.groupIndex) === groupIndex
      ) return;

      // Hide all slide-rows
      visibleRows.forEach(row => row.classList.remove('show'));

      // Find the selected product in this group
      const group = this.bundleGroups[groupIndex];
      if (!group) return;
      const selectedSwatch = group.querySelector('.product-bundle__swatch.is-selected');
      const productId = selectedSwatch?.dataset?.productId;
      if (!productId) return;

      const matchingRow = gallery.querySelector(
        `.product-bundle__slide-row[data-group-index="${groupIndex}"][data-product-id="${productId}"]`
      );
      if (matchingRow) {
        matchingRow.classList.add('show');
        matchingRow.dataset.slideIndex = 0;
        matchingRow.querySelectorAll('.product-bundle__slide').forEach((s, i) => {
          s.classList.toggle('is-active', i === 0);
        });
        // Force-load lazy images
        matchingRow.querySelectorAll('img[loading="lazy"]').forEach(img => {
          img.loading = 'eager';
        });
      }
    }

    validateBundleManager() {
      if (!this.addToCartButton) return;

      const allSelected = this.selectedVariants.every((v) => v.productId && v.variantId);
      const allInStock = this.selectedVariants.every((v) => v.variantAvailable);

      const strings = window.theme?.strings?.bundle || {};
      const addText = strings.addToCart || 'Add to cart';
      const outText = strings.outOfStock || 'Out of stock';
      const selectText = strings.selectProducts || 'Select products and sizes';

      this.updateBundlePrice();

      if (allSelected && allInStock) {
        this.addToCartButton.removeAttribute('disabled');
        const formattedPrice = this.getFormattedBundlePrice();
        this.addToCartButton.textContent = formattedPrice ? `${addText} - ${formattedPrice}` : addText;
      } else if (allSelected) {
        this.addToCartButton.setAttribute('disabled', 'disabled');
        this.addToCartButton.textContent = outText;
      } else {
        this.addToCartButton.setAttribute('disabled', 'disabled');
        this.addToCartButton.textContent = selectText;
      }
    }

    /* ------------------------------------------------------------------
       Dynamic bundle price calculation
    ------------------------------------------------------------------ */

    /**
     * Returns the effective discount percentage based on customer status.
     * Members get memberBundleDiscount if available, otherwise regular discount.
     */
    getEffectiveDiscount() {
      if (this.isMember && this.memberBundleDiscount > 0) {
        return this.memberBundleDiscount;
      }
      return this.bundleDiscount;
    }

    /**
     * Returns the formatted discounted bundle price as plain text (no HTML),
     * or empty string if not all groups have a variant selected.
     * Used for button labels where textContent is set.
     */
    getFormattedBundlePrice() {
      const allSelected = this.selectedVariants.every((v) => v.productId && v.variantId);
      if (!allSelected) return '';

      const totalCents = this.selectedVariants.reduce((sum, v) => sum + (v.variantPrice || 0), 0);
      const discount = this.getEffectiveDiscount();
      const discountedCents = Math.round(totalCents * (1 - discount / 100));

      return this.stripHtml(this.formatMoney(discountedCents));
    }

    /**
     * Updates the price wrapper in the header with the calculated bundle price.
     * Shows three price tiers:
     *  1. Førpris   — raw sum, no discounts (line-through)
     *  2. Normalpris — sum with bundle discount applied
     *  3. Medlemspris — sum with member bundle discount applied (if configured)
     * Keeps the server-rendered Liquid markup until all groups have a variant selected.
     */
    updateBundlePrice() {
      if (!this.priceWrapper) return;

      const allSelected = this.selectedVariants.every((v) => v.productId && v.variantId);
      if (!allSelected) {
        // Keep the server-rendered initial price (from Liquid) until the
        // customer has made all selections, then JS takes over.
        return;
      }

      const strings = window.theme?.strings?.bundle || {};
      const beforeLabel = strings.beforePrice || 'Before price';
      const regularLabel = strings.regularPrice || 'Regular price';
      const memberLabel = strings.memberPrice || 'Member price';

      const totalCents = this.selectedVariants.reduce((sum, v) => sum + (v.variantPrice || 0), 0);
      const bundleDiscount = this.bundleDiscount;
      const memberDiscount = this.memberBundleDiscount;

      let html = '';

      // Column 1: Førpris — always shown
      html +=
        `<div class="product-bundle__price-col product-bundle__price-col--before">` +
          `<span class="product-bundle__price product-bundle__price--before">${this.formatMoney(totalCents)}</span>` +
          `<span class="product-bundle__price-label">${beforeLabel}</span>` +
        `</div>`;

      // Column 2: Normalpris — shown if bundle discount > 0
      if (bundleDiscount > 0 && bundleDiscount <= 100) {
        const bundleCents = Math.round(totalCents * (1 - bundleDiscount / 100));
        html +=
          `<div class="product-bundle__price-divider"></div>` +
          `<div class="product-bundle__price-col product-bundle__price-col--regular">` +
            `<span class="product-bundle__price product-bundle__price--regular">${this.formatMoney(bundleCents)}</span>` +
            `<span class="product-bundle__price-label">${regularLabel}</span>` +
          `</div>`;
      }

      // Column 3: Medlemspris — shown if member discount > 0
      if (memberDiscount > 0 && memberDiscount <= 100) {
        const memberCents = Math.round(totalCents * (1 - memberDiscount / 100));
        html +=
          `<div class="product-bundle__price-divider"></div>` +
          `<div class="product-bundle__price-col product-bundle__price-col--member">` +
            `<span class="product-bundle__price product-bundle__price--member">${this.formatMoney(memberCents)}</span>` +
            `<span class="product-bundle__price-label product-bundle__price-label--member">${memberLabel}</span>` +
          `</div>`;
      }

      this.priceWrapper.innerHTML = html;
    }

    /**
     * Formats a price in cents using the theme's money format.
     * Falls back to a simple format if theme.Currency is unavailable.
     */
    formatMoney(cents) {
      if (typeof theme !== 'undefined' && theme.Currency?.formatMoney) {
        return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
      }
      // Fallback: simple decimal format
      return (cents / 100).toFixed(2);
    }

    /**
     * Strips HTML tags from a string.
     * Used when inserting formatted money into textContent (e.g. button labels).
     */
    stripHtml(html) {
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      return tmp.textContent || '';
    }

    /* ------------------------------------------------------------------
       Add to cart
    ------------------------------------------------------------------ */

    addBundleToCart() {
      // Build items array from the child variant selections.
      // Each child product is added individually as a regular cart item.
      // Uses a single POST with the items[] array to avoid rate-limit
      // issues and race conditions from concurrent cart mutations.
      const bundleHandle = this.dataset.bundleProductHandle || '';
      const bundleTitle  = this.dataset.bundleProductTitle  || '';

      const items = this.selectedVariants.map((v) => ({
        id: parseInt(v.variantId),
        quantity: 1,
        properties: {
          _bundle_product: bundleHandle,
          _bundle_product_title: bundleTitle
        }
      }));

      if (!items.length) return;

      this.addToCartButton.classList.add('is-loading');
      this.addToCartButton.setAttribute('disabled', 'disabled');

      fetch(window.theme?.routes?.cartAdd || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      })
        .then((res) => res.json())
        .then((data) => {
          this.addToCartButton.classList.remove('is-loading');

          if (data.status === 422) {
            // Error (e.g. variant out of stock, invalid ID)
            this.addToCartButton.removeAttribute('disabled');
            console.error('Bundle add-to-cart error:', data.description);
            return;
          }

          // Success — fire the standard Impulse event so the cart drawer opens
          const evt = new CustomEvent('ajaxProduct:added', {
            detail: {
              product: data.items ? data.items[0] : data,
              addToCartBtn: this.addToCartButton
            },
            bubbles: true
          });
          document.dispatchEvent(evt);

          // Fire cart:updated so the shipping progress bar and other cart UI recalculates
          document.dispatchEvent(new CustomEvent('cart:updated'));

          // Re-enable button
          this.validateBundleManager();
        })
        .catch((err) => {
          console.error('Bundle add-to-cart fetch error:', err);
          this.addToCartButton.classList.remove('is-loading');
          this.validateBundleManager();
        });
    }
  }

  customElements.define('product-bundle-manager', ProductBundleManager);
}

/**
 * A/B release: the product page's buy bar at the bottom of the screen (assets/pdp-information.css), for the bundle's
 * own button (sections/main-product-bundle.liquid). Shown while that button is out of view, it carries the button's
 * text and presses it when the bundle can go in the cart; before that, it scrolls back to the button and the choices
 * above it.
 */
(() => {
  const sticky = document.querySelector('[data-bundle-sticky]');
  const button = document.querySelector('.product-bundle__add-to-cart');
  if (!sticky || !button) return;

  // in <body>, so nothing on the page can clip it, as on the product page
  document.body.appendChild(sticky);
  const stickyButton = sticky.querySelector('[data-bundle-sticky-buy]');
  let buttonInView = true;

  // the bar steps aside while the cart drawer or the "more details" popup is open
  const sync = () => {
    const popupOpen = !!document.querySelector('.product-bundle__popup-overlay.is-visible');
    const cartOpen = !!document.querySelector('#CartDrawer.drawer--is-open');
    const on = !buttonInView && !popupOpen && !cartOpen;
    sticky.classList.toggle('is-visible', on);
    sticky.setAttribute('aria-hidden', on ? 'false' : 'true');
    stickyButton.tabIndex = on ? 0 : -1;
    document.documentElement.classList.toggle('pdp-sticky-on', on);
  };

  const mirror = () => {
    stickyButton.textContent = button.textContent.trim();
  };
  mirror();
  new MutationObserver(mirror).observe(button, { childList: true, characterData: true, subtree: true });

  if ('IntersectionObserver' in window) {
    new IntersectionObserver((entries) => {
      buttonInView = entries[entries.length - 1].isIntersecting;
      sync();
    }, { rootMargin: '-60px 0px 0px 0px' }).observe(button);
  }

  ['#CartDrawer', '.product-bundle__popup-overlay'].forEach((selector) => {
    const element = document.querySelector(selector);
    if (element) new MutationObserver(sync).observe(element, { attributes: true, attributeFilter: ['class'] });
  });

  stickyButton.addEventListener('click', () => {
    if (button.hasAttribute('disabled')) {
      button.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      button.click();
    }
  });
})();
